import { useAsyncRead } from './useAsyncRead';
import { useSession } from './useSession';
// hooks/useCommunities.ts — reactive hook for communities.
//
// Reads from communityStore reactively and exposes service mutations.
// Components must NOT import communityStore or communitiesService directly.
//
// useShallow is used on any filtering/derived-list selector to prevent
// re-renders when unrelated communities change.

import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useCommunityStore } from '@/core/stores/communityStore';
import { communitiesService } from '@/core/services/communitiesService';
import type { CreateCommunityInput } from '@/core/services/communitiesService';
import type { Community, CommunityInvite } from '@/core/models/community';

export interface CommunitiesHook {
  /** All communities in seed order. */
  communities: Community[];
  /** Only the communities the current user has joined. */
  joined: Community[];
  /** All community invites. */
  invites: CommunityInvite[];
  /** Toggle join/leave for a community by id. */
  toggleJoin: (id: string) => Promise<void>;
  /** Create a post in a community's feed; returns the new post id. */
  createCommunityPost: (communityId: string, body: string) => Promise<string>;
  /** Create a new community from dialog input; returns the new record. */
  addCommunity: (input: CreateCommunityInput) => Promise<Community>;
}

export interface CommunityHook {
  /** The community, or null when it is not (yet) known. */
  community: Community | null;
  /** True until the by-id lookup settles. Render neither the page nor "not found" while true. */
  loading: boolean;
}

export interface CommunitiesTrendingHook {
  /** Trending communities. */
  communities: Community[];
}

export interface CommunitiesRecommendedHook {
  loading?: boolean;
  error?: boolean;
  retry?: () => void;
  /** Recommended communities. */
  communities: Community[];
}

/** Primary hook: all communities + join mutation. */
export function useCommunities(): CommunitiesHook {
  // The communities list — stable store ref, no useShallow needed.
  const communities = useCommunityStore((s) => s.communities);

  // Filtered subset — returns a new array each eval, so useShallow is required.
  const joined = useCommunityStore(useShallow((s) => s.communities.filter((c) => c.joined)));

  // Invites list — stable store ref.
  const invites = useCommunityStore((s) => s.invites);

  return {
    communities,
    joined,
    invites,
    toggleJoin: (id) => communitiesService.toggleJoin(id),
    createCommunityPost: (communityId, body) =>
      communitiesService.createCommunityPost(communityId, body),
    addCommunity: (input) => communitiesService.create(input),
  };
}

/**
 * Single community by id, for the detail route.
 *
 * The store only holds what `list()` hydrated, so a deep link to a community
 * outside that list (or one opened before hydration lands) must fetch by id —
 * otherwise the page renders "not found" for a community that exists. The fetch
 * runs on every mount, not just on a store miss: the list copy carries the
 * viewer's `joined` flag and `postIds`, which go stale after a join elsewhere.
 *
 * `loading` exists so the caller can hold off rendering the terminal
 * "not found" state; a store hit renders immediately and refreshes underneath.
 */
export function useCommunity(id: string | null | undefined): CommunityHook {
  // `find` returns the store's own object reference, so the default Object.is
  // equality is correct here — no useShallow needed.
  const community = useCommunityStore((s) => s.communities.find((c) => c.id === id) ?? null);
  // Which id has finished a round-trip. Only ever set from the async settle
  // callback, never synchronously in the effect body (set-state-in-effect rule).
  const [settledId, setSettledId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    void communitiesService.get(id).finally(() => {
      if (alive) setSettledId(id);
    });
    return () => {
      alive = false;
    };
  }, [id]);

  return { community, loading: !!id && !community && settledId !== id };
}

/**
 * Filtered hook: trending communities.
 * Uses useShallow because the selector returns a filtered array.
 */
export function useCommunitiesTrending(): CommunitiesTrendingHook {
  const communities = useCommunityStore(useShallow((s) => s.communities.filter((c) => c.trending)));
  return { communities };
}

/**
 * Filtered hook: recommended communities.
 * Uses useShallow because the selector returns a filtered array.
 */
const loadRecommended = async () => {
  await communitiesService.list();
  return true;
};
export function useCommunitiesRecommended(): CommunitiesRecommendedHook {
  const { currentUser } = useSession();
  const status = useAsyncRead(loadRecommended, currentUser?.id ?? 'guest', false);
  const communities = useCommunityStore(
    useShallow((s) => s.communities.filter((c) => c.recommended)),
  );
  return { communities, loading: status.loading, error: status.error, retry: status.retry };
}
